import { useLayoutEffect, useState } from 'react'

const MIN_HEIGHT_PX = 240

// The config layout grows with its content (`min-height: 100vh`, document
// scroll), so a `height: 100%` chain above the grid resolves to `auto` and the
// grid would stretch to its full row count — every row in the DOM and no
// virtualization. Capping the grid at the viewport bottom keeps the internal
// body viewport as the scroller, which is what row virtualization and
// reach-end infinite scroll key off.
const useViewportBoundHeight = (
  ref: React.RefObject<HTMLElement>,
  enabled: boolean,
  bottomGap: number,
) => {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (!enabled) {
      setMaxHeight(undefined)
      return undefined
    }
    const el = ref.current
    if (!el) return undefined

    let frame = 0
    const measure = () => {
      frame = 0
      // A page that mounts the grid inside a scale/fade transition reports a
      // transformed rect while the animation runs. Measuring then would pin
      // the grid to the floor height for good, so wait until it is laid out.
      if (!el.isConnected || el.offsetParent === null) return
      const { top } = el.getBoundingClientRect()
      const available = window.innerHeight - top - bottomGap
      if (available <= 0) return
      const next = Math.max(MIN_HEIGHT_PX, Math.floor(available))
      setMaxHeight((prev) => (prev === next ? prev : next))
    }
    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('resize', schedule)
    // Content above the grid (toolbars, banners) can change size, and a
    // document scroll moves the grid's top edge — both shift the available
    // height.
    document.addEventListener('scroll', schedule, { capture: true, passive: true })
    // A transition on an ancestor (tab panel, card) settles into the real
    // geometry only when it ends.
    document.addEventListener('transitionend', schedule)
    const observer = new ResizeObserver(schedule)
    observer.observe(el)
    if (el.parentElement) observer.observe(el.parentElement)

    return () => {
      window.removeEventListener('resize', schedule)
      document.removeEventListener('scroll', schedule, { capture: true })
      document.removeEventListener('transitionend', schedule)
      observer.disconnect()
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [ref, enabled, bottomGap])

  return maxHeight
}

export default useViewportBoundHeight
